import React, { useState, useRef, useContext } from "react";
import Dialog from "../../components/Dialog";
import DateChoice from "../../components/DateChoice";
import Lang from "../../components/Lang";
import mut from "../../hooks/mut";
import { Calendar } from "lucide-react";
import "./LeaveRequestDialog.css";
import { LanguageContext } from "../../../../Context";

/**
 * Leave request dialog using native Dialog component with DateChoice pickers.
 * Prevents booking leave in the past and validates date ranges.
 * @author Forge
 * @date 2026-04-08
 * @version 1.0
 */
export default function LeaveRequestDialog({
  open,
  onClose,
  manager,
  onSuccess
}) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dialogInnerRef = useRef(null);
  const { l, language } = useContext(LanguageContext);

  const resetState = () => {
    setStartDate("");
    setEndDate("");
    setReason("");
    setError("");
    setSubmitting(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateDates = () => {
    if (!startDate || !endDate) {
      setError("dateRequired");
      return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("invalidDateRange");
      return false;
    }
    return true;
  };

  const handleConfirm = async () => {
    if (!validateDates()) return;
    setSubmitting(true);
    setError("");

    try {
      const res = await mut("post", "/leave-requests", {
        startDate,
        endDate,
        reason,
        manager
      });
      if (res) {
        onSuccess?.(res);
        handleClose();
      } else {
        setError("submissionFailed");
      }
    } catch (err) {
      setError("submissionFailed");
    } finally {
      setSubmitting(false);
    }
  };

  const getErrorMessage = () => {
    switch (error) {
      case "dateRequired":
        return "Both start date and end date are required";
      case "invalidDateRange":
        return "End date must be after start date";
      case "submissionFailed":
        return "Failed to submit leave request. Please try again.";
      default:
        return null;
    }
  };

  const errorMsg = getErrorMessage();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title="requestLeave"
      titleIcon={<Calendar size={18} />}
      size="xl"
      confirmLabel="submit"
      cancelLabel="cancel"
      confirmDisabled={submitting || !startDate || !endDate}
      hideFooter={false}
      innerRef={dialogInnerRef}
    >
      <div className="leave-dialog-content">
        <div className="leave-dialog-row">
          <div className="leave-dialog-field">
            <DateChoice
              value={startDate}
              setValue={(val) => {
                setStartDate(val);
                setError("");
              }}
              label="From"
              noPast={true}
              portalContainer={dialogInnerRef.current}
            />
          </div>

          <div className="leave-dialog-field">
            <DateChoice
              value={endDate}
              setValue={(val) => {
                setEndDate(val);
                setError("");
              }}
              label="To"
              noPast={true}
              portalContainer={dialogInnerRef.current}
            />
          </div>
        </div>

        <div className="leave-dialog-field">
          <label className="leave-dialog-label">
            <Lang w="reason" />
          </label>
          <textarea
            className="leave-dialog-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={l?.leaveReasonPlaceholder?.[language] || "Please give a clear reason for the leave, unclear reasons like \"I'm busy\" or \"Personal reasons\" will not be accepted"}
            rows={3}
          />
        </div>

        {errorMsg && (
          <div className="leave-dialog-error">
            {errorMsg}
          </div>
        )}
      </div>
    </Dialog>
  );
}